import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CheckCircle, AlertCircle, Users, Building, Briefcase } from 'lucide-react';
import { trackRecruitmentRequest } from '../utils/analytics';
import { HoneypotField } from '../components/HoneypotField';

const API = '/api';

export const RecruitmentRequestPage = () => {
  const { t, direction } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    position_title: '',
    department: '',
    employment_type: 'full-time',
    work_model: 'onsite',
    location: '',
    salary_min: '',
    salary_max: '',
    currency: 'EUR',
    urgency: 'normal',
    job_description: '',
    requirements: '',
    notes: '',
    website: '',  // Honeypot field
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
      };
      
      const response = await fetch(`${API}/recruitment-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        setSuccess(true);
        // Track recruitment request in Google Analytics
        trackRecruitmentRequest();
      } else {
        const data = await response.json();
        setError(data.detail || t('forms.error'));
      }
    } catch (error) {
      setError(t('forms.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#111827] mb-2">{t('forms.success')}</h2>
            <p className="text-[#6B7280] mb-6">
              Thank you for your recruitment request! Our team will review your requirements and contact you shortly.
            </p>
            <Link to="/">
              <Button className="btn-primary">{t('common.back')} to {t('nav.home')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="recruitment-request-page">
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#6B7280] hover:text-[#33AADD] mb-6"
            data-testid="back-link"
          >
            <ArrowLeft className={`h-4 w-4 me-2 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            {t('common.back')}
          </Link>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] mb-4">
            {t('cta.recruitment.button')}
          </h1>
          <p className="text-lg text-[#6B7280] max-w-2xl">
            {t('cta.recruitment.subtitle')}
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white border-b border-[#E5E7EB]">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#111827] mb-1">Access to Top Talent</h3>
                <p className="text-sm text-[#6B7280]">Our extensive network connects you with qualified candidates across industries.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                <Building className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#111827] mb-1">Industry Expertise</h3>
                <p className="text-sm text-[#6B7280]">Specialized recruiters who understand your sector's unique requirements.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                <Briefcase className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#111827] mb-1">End-to-End Support</h3>
                <p className="text-sm text-[#6B7280]">From job posting to onboarding, we manage the entire recruitment process.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container-custom max-w-3xl">
          <form onSubmit={handleSubmit}>
            {/* Honeypot anti-spam field */}
            <HoneypotField 
              value={formData.website} 
              onChange={(v) => handleChange('website', v)} 
            />
            
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">{t('forms.fullName')} *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">{t('forms.company')} *</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('forms.email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('forms.phone')} *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">{t('forms.country')} *</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    required
                    className="input-focus"
                    data-testid="input-country"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Position Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="position_title">{t('forms.position')} *</Label>
                    <Input
                      id="position_title"
                      value={formData.position_title}
                      onChange={(e) => handleChange('position_title', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-position"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">{t('forms.department')}</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => handleChange('department', e.target.value)}
                      className="input-focus"
                      data-testid="input-department"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('forms.employmentType')} *</Label>
                    <Select value={formData.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
                      <SelectTrigger data-testid="select-employment-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full-time">{t('jobs.fullTime')}</SelectItem>
                        <SelectItem value="part-time">{t('jobs.partTime')}</SelectItem>
                        <SelectItem value="contract">{t('jobs.contract')}</SelectItem>
                        <SelectItem value="internship">{t('jobs.internship')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('forms.workModel')} *</Label>
                    <Select value={formData.work_model} onValueChange={(v) => handleChange('work_model', v)}>
                      <SelectTrigger data-testid="select-work-model">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onsite">{t('jobs.onsite')}</SelectItem>
                        <SelectItem value="hybrid">{t('jobs.hybrid')}</SelectItem>
                        <SelectItem value="remote">{t('jobs.remote')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">{t('forms.location')} *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-location"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('forms.urgency')} *</Label>
                    <Select value={formData.urgency} onValueChange={(v) => handleChange('urgency', v)}>
                      <SelectTrigger data-testid="select-urgency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">{t('forms.urgent')}</SelectItem>
                        <SelectItem value="normal">{t('forms.normal')}</SelectItem>
                        <SelectItem value="low">{t('forms.low')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="salary_min">{t('forms.salaryMin')}</Label>
                    <Input
                      id="salary_min"
                      type="number"
                      min="0"
                      value={formData.salary_min}
                      onChange={(e) => handleChange('salary_min', e.target.value)}
                      className="input-focus"
                      data-testid="input-salary-min"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salary_max">{t('forms.salaryMax')}</Label>
                    <Input
                      id="salary_max"
                      type="number"
                      min="0"
                      value={formData.salary_max}
                      onChange={(e) => handleChange('salary_max', e.target.value)}
                      className="input-focus"
                      data-testid="input-salary-max"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => handleChange('currency', v)}>
                      <SelectTrigger data-testid="select-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="AED">AED</SelectItem>
                        <SelectItem value="SAR">SAR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="job_description">{t('forms.jobDescription')} *</Label>
                  <Textarea
                    id="job_description"
                    value={formData.job_description}
                    onChange={(e) => handleChange('job_description', e.target.value)}
                    required
                    rows={5}
                    className="input-focus"
                    data-testid="input-job-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="requirements">{t('forms.requirements')}</Label>
                  <Textarea
                    id="requirements"
                    value={formData.requirements}
                    onChange={(e) => handleChange('requirements', e.target.value)}
                    rows={3}
                    className="input-focus"
                    data-testid="input-requirements"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">{t('forms.notes')}</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={3}
                    className="input-focus"
                    data-testid="input-notes"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 p-3 bg-red-50 rounded-lg">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={submitting}
                  data-testid="submit-btn"
                >
                  {submitting ? t('forms.sending') : t('forms.submit')}
                </Button>
              </CardContent>
            </Card>
          </form>
        </div>
      </section>
    </div>
  );
};

export default RecruitmentRequestPage;
